import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { Doc as YDoc } from 'yjs';

import { useStore } from 'utils/hooks/store';
import Logger from 'utils/logger';
import { getColorFromInitials } from 'components/Avatar/utils';
import { getUrlParam, TIPTAP_COLLAB_APP_ID } from 'containers/Tiptap/lib/tiptap-collab-utils';
import DocumentsDetailsStore from 'stores/DocumentsDetails/DocumentsDetails';

const getCollabToken = (documentsDetails: DocumentsDetailsStore, documentId: string) => async (): Promise<string> => {
  try {
    await documentsDetails.loadDocumentToken(documentId);
    return documentsDetails.documentToken || '';
  } catch (e) {
    Logger.error('Failed to fetch Tiptap collab token', e);
    return '';
  }
};

export type CollabContextValue = {
  provider: TiptapCollabProvider | null;
  ydoc: YDoc;
  hasCollab: boolean;
  setupError: boolean;
};

export const CollabContext = createContext<CollabContextValue>({
  hasCollab: false,
  provider: null,
  ydoc: new YDoc(),
  setupError: false,
});

export const CollabConsumer = CollabContext.Consumer;
export const useCollab = (): CollabContextValue => {
  const context = useContext(CollabContext);
  if (!context) {
    throw new Error('useCollab must be used within an CollabProvider');
  }
  return context;
};

export const useCollaboration = (
  room: string,
  organisation?: string
): {
  provider: TiptapCollabProvider | null;
  ydoc: YDoc;
  hasCollab: boolean;
  setupError: boolean;
} => {
  const { session, documentsDetails } = useStore();
  const [provider, setProvider] = useState<TiptapCollabProvider | null>(null);
  const [hasCollab, setHasCollab] = useState<boolean>(true);
  const [setupError, setSetupError] = useState<boolean>(false);
  const ydoc = useMemo(() => new YDoc(), []);

  const hasTiptapAccess = Boolean(documentsDetails.documentToken);
  const orgUuid = session.user?.organisation.uuid;

  useEffect(() => {
    const noCollabParam = getUrlParam('noCollab');
    setHasCollab(parseInt(noCollabParam || '0') !== 1);
  }, []);

  useEffect(() => {
    if (!hasCollab) return undefined;

    if (!hasTiptapAccess) {
      setSetupError(true);
      return undefined;
    }

    setSetupError(false);

    const documentName = `${organisation || orgUuid}/${room}`;

    const newProvider = new TiptapCollabProvider({
      name: documentName,
      appId: TIPTAP_COLLAB_APP_ID,
      token: getCollabToken(documentsDetails, room),
      document: ydoc,
    });

    // Set user awareness data
    const userName = session.user?.name || 'Anonymous';
    const nameParts = userName.trim().toUpperCase().split(' ');
    const firstName = nameParts[0]?.charAt(0) || 'A';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : firstName;
    const userColor = getColorFromInitials(firstName, lastName) || '#b616e4';

    newProvider.setAwarenessField('user', {
      name: userName,
      color: userColor,
      clientId: session.user?.uuid || 'anonymous',
    });

    setProvider(newProvider);

    return () => {
      newProvider.destroy();
    };
  }, [
    hasTiptapAccess,
    ydoc,
    room,
    hasCollab,
    organisation,
    orgUuid,
    session.user?.name,
    session.user?.uuid,
    documentsDetails,
  ]);

  return { provider, ydoc, hasCollab, setupError };
};

export function CollabProvider({
  children,
  room,
  organisation,
}: Readonly<{
  children: React.ReactNode;
  room: string;
  organisation?: string;
}>): JSX.Element {
  const { hasCollab, provider, ydoc, setupError } = useCollaboration(room, organisation);

  const value = useMemo<CollabContextValue>(
    () => ({
      hasCollab,
      provider,
      ydoc,
      setupError,
    }),
    [hasCollab, provider, ydoc, setupError]
  );

  return <CollabContext.Provider value={value}>{children}</CollabContext.Provider>;
}
